import { createApi } from "@reduxjs/toolkit/query/react";
import { baseApiQuery } from "../../../shared/api";
import { BaseResponse } from "../../../shared/types";
import {
  CategoriesResponse,
  CategoryResponse,
  CreateCategoryRequest,
  CreateNewsRequest,
  GetNewsCommentsParams,
  NewsCommentsResponse,
  NewsListResponse,
  NewsResponse,
  UpdateCategoryRequest,
  UpdateNewsRequest,
  UrlResponse,
} from "./types";

export interface GetNewsParams {
  search?: string;
  sort?: string;
  order?: "asc" | "desc";
  pageToken?: string;
  limit?: number;
}

export const newsApi = createApi({
  reducerPath: "newsApi",
  baseQuery: baseApiQuery,
  tagTypes: ["News", "NewsCategories", "NewsComments"],
  endpoints: (builder) => ({
    getNews: builder.query<NewsListResponse, GetNewsParams>({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params.search) queryParams.append("search", params.search);
        if (params.sort) queryParams.append("sort", params.sort);
        if (params.order) queryParams.append("order", params.order);
        if (params.pageToken) queryParams.append("pageToken", params.pageToken);
        if (params.limit) queryParams.append("limit", params.limit.toString());

        return `/admin/news${
          queryParams.toString() ? `?${queryParams.toString()}` : ""
        }`;
      },
      providesTags: ["News"],
    }),

    getNewsById: builder.query<NewsResponse, number>({
      query: (id) => `/news/${id}`,
      providesTags: (_result, _error, id) => [{ type: "News", id }],
    }),

    createNews: builder.mutation<NewsResponse, CreateNewsRequest>({
      query: (body) => ({
        url: "/admin/news",
        method: "POST",
        body,
      }),
      invalidatesTags: ["News", "NewsCategories"],
    }),

    updateNews: builder.mutation<
      NewsResponse,
      { id: number; body: UpdateNewsRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/news/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["News"],
    }),

    deleteNews: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/news/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["News"],
    }),

    publishNews: builder.mutation<NewsResponse, number>({
      query: (id) => ({
        url: `/admin/news/${id}/publish`,
        method: "PUT",
      }),
      invalidatesTags: ["News"],
    }),

    uploadNewsImage: builder.mutation<
      UrlResponse,
      { id: number; file: FormData }
    >({
      query: ({ id, file }) => ({
        url: `/admin/news/${id}/image`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["News"],
    }),

    deleteNewsImage: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/news/${id}/image`,
        method: "DELETE",
      }),
      invalidatesTags: ["News"],
    }),

    uploadNewsVideo: builder.mutation<
      UrlResponse,
      { id: number; file: FormData }
    >({
      query: ({ id, file }) => ({
        url: `/admin/news/${id}/video`,
        method: "POST",
        body: file,
      }),
      invalidatesTags: ["News"],
    }),

    deleteNewsVideo: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/news/${id}/video`,
        method: "DELETE",
      }),
      invalidatesTags: ["News"],
    }),

    getNewsCategories: builder.query<CategoriesResponse, void>({
      query: () => "/admin/news/categories",
      providesTags: ["NewsCategories"],
    }),

    createNewsCategory: builder.mutation<
      CategoryResponse,
      CreateCategoryRequest
    >({
      query: (body) => ({
        url: "/admin/news/categories",
        method: "POST",
        body,
      }),
      invalidatesTags: ["NewsCategories"],
    }),

    updateNewsCategory: builder.mutation<
      CategoryResponse,
      { id: number; body: UpdateCategoryRequest }
    >({
      query: ({ id, body }) => ({
        url: `/admin/news/categories/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["NewsCategories"],
    }),

    deleteNewsCategory: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/news/categories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["NewsCategories"],
    }),

    getNewsComments: builder.query<NewsCommentsResponse, GetNewsCommentsParams>({
      query: ({ id, allAsList, pageToken, limit }) => {
        const params = new URLSearchParams();
        if (allAsList !== undefined)
          params.append("allAsList", String(allAsList));
        if (pageToken) params.append("pageToken", pageToken);
        if (limit != null) params.append("limit", String(limit));
        return `/admin/news/${id}/comments${
          params.toString() ? `?${params.toString()}` : ""
        }`;
      },
      providesTags: (_result, _error, { id }) => [
        { type: "NewsComments", id: String(id) },
      ],
    }),

    deleteNewsComment: builder.mutation<
      BaseResponse,
      { id: number; commentId: number }
    >({
      query: ({ id, commentId }) => ({
        url: `/admin/news/${id}/comments/${commentId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        "News",
        { type: "NewsComments", id: String(id) },
      ],
    }),

    sendNewsPreviewEmail: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/news/${id}/preview/email`,
        method: "POST",
      }),
    }),

    sendNewsPreviewPush: builder.mutation<BaseResponse, number>({
      query: (id) => ({
        url: `/admin/news/${id}/preview/push`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetNewsQuery,
  useGetNewsByIdQuery,
  useCreateNewsMutation,
  useUpdateNewsMutation,
  useDeleteNewsMutation,
  usePublishNewsMutation,
  useUploadNewsImageMutation,
  useDeleteNewsImageMutation,
  useUploadNewsVideoMutation,
  useDeleteNewsVideoMutation,
  useGetNewsCategoriesQuery,
  useCreateNewsCategoryMutation,
  useUpdateNewsCategoryMutation,
  useDeleteNewsCategoryMutation,
  useGetNewsCommentsQuery,
  useDeleteNewsCommentMutation,
  useSendNewsPreviewEmailMutation,
  useSendNewsPreviewPushMutation,
} = newsApi;
